import { StrictMode, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { Auth0Provider } from "@auth0/auth0-react";
import App from "./App";
import type { PaletteMode } from "@mui/material";
import { createAppTheme } from "./theme";
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_AUDIENCE } from "./env";

const THEME_MODE_STORAGE_KEY = "zennet.fleet.theme-mode";

function readInitialThemeMode(): PaletteMode {
  const value = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return value === "dark" ? "dark" : "light";
}

function Root() {
  const [themeMode, setThemeMode] = useState<PaletteMode>(readInitialThemeMode);
  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  const toggleThemeMode = () => {
    setThemeMode((current) => {
      const next = current === "dark" ? "light" : "dark";
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
      return next;
    });
  };

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: AUTH0_AUDIENCE || undefined,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App themeMode={themeMode} onToggleThemeMode={toggleThemeMode} />
      </ThemeProvider>
    </Auth0Provider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
